import { useEffect, useRef, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import {
  DepartmentSchema,
  EmployeeSchema,
  OrganizationSchema,
  PositionSchema,
  type DepartmentDto,
  type EmployeeDto,
} from '@taqon/contracts';
import type { z } from 'zod';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { getData, getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { RecordLink } from '../finance/detail';
import { DEPARTMENTS } from './MastersPage';

const PAGE_SIZE = 200;

/** Every row of a list, page by page (the API serves at most 200 a page, SDD §10.4). */
async function getAll<S extends z.ZodType>(path: string, schema: S): Promise<z.infer<S>[]> {
  const items: z.infer<S>[] = [];
  for (let page = 1; ; page++) {
    const sep = path.includes('?') ? '&' : '?';
    const result = await getPage(`${path}${sep}page=${page}&pageSize=${PAGE_SIZE}`, schema);
    items.push(...result.items);
    if (result.items.length < PAGE_SIZE || items.length >= result.total) return items;
  }
}

export interface DepartmentNode {
  readonly department: DepartmentDto;
  readonly children: readonly DepartmentNode[];
}

/**
 * The active departments as a tree, by code at each level. A department whose parent is not among
 * them (an inactive parent) is shown at the top level rather than hidden.
 */
export function departmentTree(departments: readonly DepartmentDto[]): DepartmentNode[] {
  const ids = new Set(departments.map((d) => d.id));
  const byParent = new Map<string | null, DepartmentDto[]>();
  for (const department of departments) {
    const parent =
      department.parentDepartmentId !== null && ids.has(department.parentDepartmentId)
        ? department.parentDepartmentId
        : null;
    byParent.set(parent, [...(byParent.get(parent) ?? []), department]);
  }
  const build = (parent: string | null, seen: ReadonlySet<string>): DepartmentNode[] =>
    (byParent.get(parent) ?? [])
      .filter((d) => !seen.has(d.id))
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((department) => ({
        department,
        children: build(department.id, new Set([...seen, department.id])),
      }));
  return build(null, new Set());
}

/**
 * The organization chart (FRD-HRP-008, FRD-CORE-002): the legal entity and its active departments
 * as they sit under one another. Choosing a department lists its active employees, each linked to
 * the employee's own page. It reads the existing lists, so each person sees only the departments
 * and employees their permissions already show; the API decides, as everywhere.
 */
export function OrganizationChartPage() {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('department');

  const organization = useQuery({
    queryKey: ['organization'],
    enabled: can('organization.organization:view'),
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });
  const departments = useQuery({
    queryKey: ['departments', 'chart'],
    queryFn: () => getAll(`${DEPARTMENTS.path}?sort=code&filter[status]=active`, DepartmentSchema),
  });
  const canSeeEmployees = can('hr.employee:view');
  const employees = useQuery({
    queryKey: ['hr', 'employees', 'chart'],
    enabled: canSeeEmployees,
    queryFn: () => getAll('/api/v1/hr/employees?sort=name&filter[status]=active', EmployeeSchema),
  });
  const positions = useQuery({
    queryKey: ['hr', 'positions'],
    enabled: can('hr.position:view'),
    queryFn: () => getPage('/api/v1/hr/positions?pageSize=200', PositionSchema),
  });

  const counts = new Map<string, number>();
  for (const employee of employees.data ?? []) {
    counts.set(employee.departmentId, (counts.get(employee.departmentId) ?? 0) + 1);
  }
  const tree = departmentTree(departments.data ?? []);
  const selected = departments.data?.find((d) => d.id === selectedId);
  const select = (id: string | null) => {
    setParams(id === null ? {} : { department: id }, { replace: false });
  };
  const root = organization.data;

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Organization chart"
        description="The departments of the organization as they sit under one another. Choose a department to see its employees."
        actions={
          <Button component={RouterLink} to="/organization" startIcon={<ArrowBackIcon />}>
            Legal entity
          </Button>
        }
      />
      <Section title="Structure">
        {departments.isPending && <LoadingState label="Loading the departments" />}
        {departments.isError && (
          <Box sx={{ p: 2 }}>
            <ErrorState message={departments.error.message} />
          </Box>
        )}
        {departments.data !== undefined && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: { md: 'center' } }}>
              <Box
                sx={(theme) => ({
                  px: 2.5,
                  py: 1.5,
                  borderRadius: 2,
                  color: 'common.white',
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#1f2a2c',
                  textAlign: { md: 'center' },
                  maxWidth: 420,
                })}
              >
                <Typography variant="subtitle1" component="p" sx={{ fontWeight: 700 }}>
                  {root?.code ?? 'Organization'}
                </Typography>
                {root !== undefined && (
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {root.legalName}
                  </Typography>
                )}
              </Box>
            </Box>
            {tree.length > 0 && (
              // The stub down from the organization to the line over the top-level departments.
              <Box
                aria-hidden
                sx={(theme) => ({
                  display: { xs: 'none', md: 'block' },
                  height: 16,
                  width: '50%',
                  borderRight: `2px solid ${theme.palette.divider}`,
                })}
              />
            )}
            {tree.length === 0 ? (
              <EmptyState title="No active departments" />
            ) : (
              <Box
                role="tree"
                aria-label="Departments"
                sx={{
                  mt: { xs: 2, md: 0 },
                  display: { xs: 'block', md: 'grid' },
                  gridAutoFlow: 'column',
                  gridAutoColumns: 'minmax(170px, 1fr)',
                  overflowX: 'auto',
                }}
              >
                {tree.map((node, index) => (
                  <TopLevelColumn
                    key={node.department.id}
                    node={node}
                    first={index === 0}
                    last={index === tree.length - 1}
                    counts={employees.data === undefined ? null : counts}
                    selectedId={selectedId}
                    onSelect={select}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Section>
      {selectedId !== null && departments.data !== undefined && (
        <DepartmentEmployees
          department={selected ?? null}
          subDepartments={
            selected === undefined ? [] : (findNode(tree, selected.id)?.children ?? [])
          }
          canSeeEmployees={canSeeEmployees}
          employees={employees}
          positionTitles={
            new Map((positions.data?.items ?? []).map((p) => [p.id, p.title] as const))
          }
          counts={employees.data === undefined ? null : counts}
          onSelect={select}
        />
      )}
    </Stack>
  );
}

function findNode(nodes: readonly DepartmentNode[], id: string): DepartmentNode | undefined {
  for (const node of nodes) {
    if (node.department.id === id) return node;
    const found = findNode(node.children, id);
    if (found !== undefined) return found;
  }
  return undefined;
}

interface NodeProps {
  readonly counts: ReadonlyMap<string, number> | null;
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

/**
 * A top-level department and everything under it. On a wide screen the top-level departments
 * stand side by side under the organization, joined by connector lines; on a phone the chart reads
 * as an indented list.
 */
function TopLevelColumn({
  node,
  first,
  last,
  ...props
}: NodeProps & { node: DepartmentNode; first: boolean; last: boolean }) {
  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        pb: 1.5,
        // One media block: responsive shorthands here would share its key and be overwritten.
        [theme.breakpoints.up('md')]: {
          px: 1,
          pt: 2,
          pb: 0,
          // The bus from the organization: a horizontal line over the columns, and a stub down to
          // each department. The first and last columns draw only their inner half.
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: first ? '50%' : 0,
            right: last ? '50%' : 0,
            borderTop: `2px solid ${theme.palette.divider}`,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            height: 16,
            borderLeft: `2px solid ${theme.palette.divider}`,
          },
          ...(first && last ? { '&::before': { content: 'none' } } : {}),
        },
      })}
    >
      <DepartmentButton node={node} level={1} {...props} />
      {node.children.length > 0 && <SubTree nodes={node.children} level={2} {...props} />}
    </Box>
  );
}

function SubTree({
  nodes,
  level,
  ...props
}: NodeProps & { nodes: readonly DepartmentNode[]; level: number }) {
  return (
    <Box
      role="group"
      sx={(theme) => ({
        ml: 2,
        pl: 2,
        mt: 1,
        borderLeft: `2px solid ${theme.palette.divider}`,
        display: 'grid',
        gap: 1,
      })}
    >
      {nodes.map((child) => (
        <Box
          key={child.department.id}
          sx={(theme) => ({
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -16,
              top: 22,
              width: 14,
              borderTop: `2px solid ${theme.palette.divider}`,
            },
          })}
        >
          <DepartmentButton node={child} level={level} {...props} />
          {child.children.length > 0 && (
            <SubTree nodes={child.children} level={level + 1} {...props} />
          )}
        </Box>
      ))}
    </Box>
  );
}

function DepartmentButton({
  node,
  level,
  counts,
  selectedId,
  onSelect,
}: NodeProps & { node: DepartmentNode; level: number }) {
  const { department } = node;
  const selected = department.id === selectedId;
  const direct = counts?.get(department.id) ?? 0;
  const total = counts === null ? 0 : totalOf(node, counts);
  return (
    <ButtonBase
      role="treeitem"
      aria-level={level}
      aria-selected={selected}
      aria-label={`${department.code} — ${department.name}${
        counts === null ? '' : `, ${peopleText(direct, total)}`
      }`}
      onClick={() => {
        onSelect(department.id);
      }}
      sx={(theme) => ({
        width: '100%',
        display: 'block',
        textAlign: 'left',
        px: 1.5,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
        borderTop: level === 1 ? `3px solid ${theme.palette.primary.main}` : undefined,
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.12)
          : level === 1
            ? 'background.paper'
            : alpha(theme.palette.text.primary, 0.02),
        boxShadow: level === 1 ? 1 : 0,
        transition: theme.transitions.create(['background-color', 'border-color']),
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}` },
      })}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}
      >
        {department.code}
      </Typography>
      <Typography
        variant={level === 1 ? 'subtitle2' : 'body2'}
        component="span"
        sx={{ display: 'block' }}
      >
        {department.name}
      </Typography>
      {counts !== null && (
        <Typography
          variant="caption"
          component="span"
          sx={{ display: 'block', color: 'text.secondary' }}
        >
          {peopleText(direct, total)}
        </Typography>
      )}
    </ButtonBase>
  );
}

function ManagerCell({
  manager,
}: {
  manager: { id: string; code: string; name: string } | undefined;
}) {
  if (manager === undefined) return '—';
  return (
    <PersonName name={manager.name} employeeId={manager.id}>
      {manager.code} — {manager.name}
    </PersonName>
  );
}

const peopleLabel = (count: number) => (count === 1 ? '1 employee' : `${String(count)} employees`);

/** Everyone in a department and the departments under it. */
function totalOf(node: DepartmentNode, counts: ReadonlyMap<string, number>): number {
  return node.children.reduce(
    (sum, child) => sum + totalOf(child, counts),
    counts.get(node.department.id) ?? 0,
  );
}

/** A department's people, its sub-departments included; says how many sit directly in it. */
function peopleText(direct: number, total: number): string {
  if (direct === total) return peopleLabel(total);
  return `${peopleLabel(total)}, ${direct === 0 ? 'none' : String(direct)} directly`;
}

function DepartmentEmployees({
  department,
  subDepartments,
  canSeeEmployees,
  employees,
  positionTitles,
  counts,
  onSelect,
}: {
  department: DepartmentDto | null;
  subDepartments: readonly DepartmentNode[];
  counts: ReadonlyMap<string, number> | null;
  canSeeEmployees: boolean;
  employees: {
    data: EmployeeDto[] | undefined;
    isPending: boolean;
    isError: boolean;
    error: Error | null;
  };
  positionTitles: ReadonlyMap<string, string>;
  onSelect: (id: string | null) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const departmentId = department?.id;
  useEffect(() => {
    const box = ref.current;
    // jsdom has no scrollIntoView.
    if (box !== null && 'scrollIntoView' in box)
      box.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [departmentId]);

  if (department === null) {
    return (
      <Section title="Department">
        <EmptyState
          title="No such department"
          detail="It is not an active department, or you may not see it."
        />
      </Section>
    );
  }
  const all = employees.data ?? [];
  const byId = new Map(all.map((e) => [e.id, e] as const));
  const members = all.filter((e) => e.departmentId === department.id);
  let body: ReactNode;
  if (!canSeeEmployees) {
    body = (
      <EmptyState
        title="You may not see employees"
        detail="Seeing who works in a department needs the permission to view employees."
      />
    );
  } else if (employees.isPending) {
    body = <LoadingState label="Loading the employees" />;
  } else if (employees.isError) {
    body = (
      <Box sx={{ p: 2 }}>
        <ErrorState message={employees.error?.message ?? 'The employees could not be read.'} />
      </Box>
    );
  } else if (members.length === 0) {
    const inSubDepartments =
      counts === null ? 0 : subDepartments.reduce((sum, child) => sum + totalOf(child, counts), 0);
    body = (
      <EmptyState
        title="No active employees directly in this department"
        {...(inSubDepartments > 0
          ? {
              detail: `Its sub-departments have ${peopleLabel(inSubDepartments)}: choose a sub-department above to see them.`,
            }
          : subDepartments.length > 0
            ? { detail: 'Its sub-departments have no active employees either.' }
            : {})}
      />
    );
  } else {
    body = (
      <Table size="small" aria-label={`Employees of ${department.name}`}>
        <TableHead>
          <TableRow>
            <TableCell>Employee ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Position</TableCell>
            <TableCell>Reporting manager</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((employee) => (
            <TableRow key={employee.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{employee.code}</TableCell>
              <TableCell>
                <PersonName name={employee.name} employeeId={employee.id}>
                  <RecordLink to={`/hr/employees/${employee.id}`}>{employee.name}</RecordLink>
                </PersonName>
              </TableCell>
              <TableCell>
                {employee.positionId === null
                  ? '—'
                  : (positionTitles.get(employee.positionId) ?? '—')}
              </TableCell>
              <TableCell>
                <ManagerCell manager={byId.get(employee.reportingManagerId ?? '')} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Box ref={ref} sx={{ scrollMarginTop: 80 }}>
      <Section
        title={`${department.code} — ${department.name}`}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              component={RouterLink}
              to={`${DEPARTMENTS.route}/${department.id}`}
              size="small"
            >
              Open department
            </Button>
            <Button
              size="small"
              onClick={() => {
                onSelect(null);
              }}
            >
              Close
            </Button>
          </Stack>
        }
      >
        {subDepartments.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ px: 2.5, pt: 2, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              Sub-departments:
            </Typography>
            {subDepartments.map((child) => (
              <Chip
                key={child.department.id}
                label={`${child.department.code} — ${child.department.name}${
                  counts === null ? '' : ` (${String(totalOf(child, counts))})`
                }`}
                size="small"
                variant="outlined"
                onClick={() => {
                  onSelect(child.department.id);
                }}
              />
            ))}
          </Stack>
        )}
        {body}
      </Section>
    </Box>
  );
}
